import { useRef, useEffect, useState } from "react";
import { Play } from "lucide-react";

// Backend URL for static file access
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to convert relative static paths to full URLs
const getMediaUrl = (path) => {
  if (!path) return "";
  // If already a full URL or data URL, return as-is
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  // Prepend backend URL for relative paths
  return `${BACKEND_URL}${path}`;
};

export const MediaSidebar = ({
  // Match media props
  matchId,
  match,
  matchImages,
  leftImageIndex = 0,
  rightImageIndex = 1,
  matchVideos,
  currentVideoIndex,
  onVideoIndexChange,
  onLeftImageClick,
  onRightImageClick,
  onVideoPlay,
  onVideoEnded,
  videoPlaying,
  videoRef,
  // Advice chat media props
  selectedMatchForAdvice,
  selectedMatchData,
  adviceUseUserData,
  user,
}) => {
  // Handle video play
  const handleVideoPlay = () => {
    if (onVideoPlay) {
      onVideoPlay();
    }
  };

  // Handle video ended
  const handleVideoEnded = () => {
    if (onVideoEnded) {
      onVideoEnded();
    }
  };

  // Match media section
  if (matchId) {
    // Helper to get media source (supports both old data format and new src/file_path format)
    const getMediaSrc = (media) => getMediaUrl(media?.src || media?.data || "");
    const getThumbnailSrc = (media) => getMediaUrl(media?.thumbnail || "");
    
    // Get current images based on indices
    const leftImage = matchImages[leftImageIndex];
    const rightImage = matchImages[rightImageIndex];
    
    return (
      <>
        {/* Left Side Image - Full height between header and footer */}
        {leftImage && (
          <div 
            className="fixed left-0 z-10 hidden xl:flex items-stretch cursor-pointer group"
            style={{ 
              top: '64px',
              bottom: '80px'
            }}
            onClick={onLeftImageClick}
            title={`Click for next image (${leftImageIndex + 1}/${matchImages.length})`}
          >
            <div className="relative h-full overflow-hidden border-r border-white/10 hover:border-white/30 transition-all">
              <img 
                src={getMediaSrc(leftImage)} 
                alt={match?.name} 
                className="h-full w-auto object-cover"
              />
              {/* Image counter indicator */}
              {matchImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {matchImages.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full ${idx === leftImageIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Right Side Video Player - Full height between header and footer */}
        {matchVideos.length > 0 && (
          <div 
            className="fixed right-0 z-20 hidden xl:flex items-stretch"
            style={{ 
              top: '64px',
              bottom: '80px'
            }}
          >
            <div className="relative h-full overflow-hidden bg-black">
              {/* Video Element */}
              <video
                ref={videoRef}
                src={getMediaSrc(matchVideos[currentVideoIndex])}
                poster={getThumbnailSrc(matchVideos[currentVideoIndex])}
                className="h-full w-auto object-contain"
                muted
                playsInline
                preload="metadata"
                onEnded={handleVideoEnded}
              />
              
              {/* Play button overlay - only show when not playing */}
              {!videoPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer hover:bg-black/20 transition-colors"
                  onClick={handleVideoPlay}
                >
                  <div className="w-20 h-20 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-all hover:scale-110">
                    <Play className="w-10 h-10 text-white ml-1" />
                  </div>
                </div>
              )}
              
              {/* Video counter dots - show if multiple videos */}
              {matchVideos.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {matchVideos.map((_, idx) => (
                    <button 
                      key={idx}
                      onClick={() => onVideoIndexChange(idx)}
                      className={`w-3 h-3 rounded-full transition-all cursor-pointer hover:scale-125 ${
                        idx === currentVideoIndex ? 'bg-divine-gold' : 'bg-white/40 hover:bg-white/60'
                      }`}
                      title={`Video ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Right Side Image - Full height between header and footer (only if NO videos) */}
        {matchVideos.length === 0 && rightImage && (
          <div 
            className="fixed right-0 z-10 hidden xl:flex items-stretch cursor-pointer group"
            style={{ 
              top: '64px',
              bottom: '80px'
            }}
            onClick={onRightImageClick}
            title={`Click for next image (${rightImageIndex + 1}/${matchImages.length})`}
          >
            <div className="relative h-full overflow-hidden border-l border-white/10 hover:border-white/30 transition-all">
              <img 
                src={getMediaSrc(rightImage)} 
                alt={match?.name} 
                className="h-full w-auto object-cover"
              />
              {/* Image counter indicator */}
              {matchImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {matchImages.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full ${idx === rightImageIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Advice chat media section (non-match)
  return (
    <>
      {/* Center Background - Show selected match's avatar OR user's avatar */}
      {selectedMatchForAdvice && selectedMatchData?.picture ? (
        <div 
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
          style={{ height: '70vh', width: 'auto', opacity: 0.3 }}
        >
          <div className="relative h-full rounded-xl overflow-hidden">
            <img 
              src={selectedMatchData.picture}
              alt={selectedMatchData?.name || "Match"}
              className="h-full w-auto object-contain"
            />
          </div>
        </div>
      ) : !selectedMatchForAdvice && adviceUseUserData && user?.picture ? (
        <div 
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
          style={{ height: '70vh', width: 'auto', opacity: 0.3 }}
        >
          <div className="relative h-full rounded-xl overflow-hidden">
            <img 
              src={user.picture}
              alt={user?.name || "You"}
              className="h-full w-auto object-contain"
            />
          </div>
        </div>
      ) : null}
      
      {/* Left Side - Agent Eros Image */}
      <div 
        className="fixed left-4 top-1/2 -translate-y-1/2 z-0 hidden xl:block pointer-events-none"
        style={{ height: '70vh', width: 'auto' }}
      >
        <div className="relative h-full rounded-xl overflow-hidden border-2 border-white/10 shadow-2xl">
          <img 
            src="https://customer-assets.emergentagent.com/job_relategen/artifacts/r14sr32f_AgentCupid_fb-FR.jpg"
            alt="Agent Eros"
            className="h-full w-auto object-contain"
          />
        </div>
      </div>
      
      {/* Right Side - Agent Eros Image (Male) */}
      <div 
        className="fixed right-4 top-1/2 -translate-y-1/2 z-0 hidden xl:block pointer-events-none"
        style={{ height: '70vh', width: 'auto' }}
      >
        <div className="relative h-full rounded-xl overflow-hidden border-2 border-white/10 shadow-2xl">
          <img 
            src="https://customer-assets.emergentagent.com/job_relategen/artifacts/aebf5qkd_AgentCupid_fb-ML.jpg"
            alt="Agent Eros"
            className="h-full w-auto object-contain"
          />
        </div>
      </div>
    </>
  );
};

export default MediaSidebar;
